import { useState } from 'react';
import {
  NoticeSchedule,
  NoticeScheduleNoticeType,
  useGetNoticeScheduleBoQuery
} from '@generated/graphql';
import { ModalType } from '@routes/Common/Dialog/UpdateNoticeModal/updateNoticeContents';
import {
  deleteStorageItem,
  getStorageItem,
  STORAGE_ACTION
} from '@utils/storage';

export interface IUpdateModalState
  extends Pick<NoticeSchedule, 'startTime' | 'endTime'> {
  modalType: ModalType;
  isOpenInit: boolean;
  isStart: boolean;
}
interface IsNotAgainObject
  extends Pick<NoticeSchedule, 'startTime' | 'endTime'> {
  isNotSeeAgain: boolean;
  modalType: ModalType;
}
const useNoticeSchedule = () => {
  const [
    updateModalState,
    setUpdateModalState
  ] = useState<IUpdateModalState | null>(null);

  const isNotSeeingAgainString = getStorageItem(
    STORAGE_ACTION.NOT_SEEING_AGAIN_NOTICE
  );
  const isNotSeeingAgainObject: IsNotAgainObject | null =
    isNotSeeingAgainString && JSON.parse(isNotSeeingAgainString);

  useGetNoticeScheduleBoQuery({
    fetchPolicy: 'network-only',
    skip:
      window.location.host.includes('test') ||
      window.location.pathname.includes('/blog'),
    onCompleted: ({ getNoticeScheduleBO }) => {
      if (!getNoticeScheduleBO.data) {
        return;
      }
      const modalTypeAndStart = getModalTypeAndStart(getNoticeScheduleBO.data);

      if (!modalTypeAndStart) {
        return;
      }

      const { startTime, endTime } = getNoticeScheduleBO.data;

      const nextUpdateModalState = {
        startTime,
        endTime,
        ...modalTypeAndStart
      };

      const isOpenInit = getIsOpen(
        isNotSeeingAgainObject,
        nextUpdateModalState
      );

      setUpdateModalState({ ...nextUpdateModalState, isOpenInit });
    }
  });

  return updateModalState;
};

export default useNoticeSchedule;

const getModalTypeAndStart = ({
  startTime,
  previewStartTime,
  previewEndTime,
  noticeType
}: Pick<
  NoticeSchedule,
  'noticeType' | 'startTime' | 'previewStartTime' | 'previewEndTime'
>): {
  modalType: ModalType;
  isStart: boolean;
} | null => {
  const currentTime = new Date();
  const modalType =
    noticeType === NoticeScheduleNoticeType.EmergencyNotice
      ? ModalType.Emergency
      : ModalType.Update;

  if (new Date(startTime.replace(' ', 'T')) < currentTime) {
    return {
      modalType,
      isStart: true
    };
  }
  if (
    previewStartTime &&
    new Date(previewStartTime.replace(' ', 'T')) < currentTime &&
    new Date(previewEndTime!.replace(' ', 'T')) > currentTime
  ) {
    return {
      modalType: ModalType.Preview,
      isStart: true
    };
  }
  return null;
};

const getIsOpen = (
  isNotSeeingAgainObject: IsNotAgainObject | null,
  current: any
) => {
  if (!isNotSeeingAgainObject || isChanged(isNotSeeingAgainObject, current)) {
    deleteStorageItem(STORAGE_ACTION.NOT_SEEING_AGAIN_NOTICE);
    return true;
  }

  return !isNotSeeingAgainObject.isNotSeeAgain;
};

const isChanged = (past: IsNotAgainObject, current: IUpdateModalState) =>
  past.endTime !== current.endTime ||
  past.startTime !== current.startTime ||
  past.modalType !== current.modalType;
